import { Fragment, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Badge, Button, Center, Container, FileButton, Group, Loader, Stack, Table,
  Text, TextInput, Timeline, Title,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import {
  psPing, psSearch, psDevice, psUploadXlsx, psExport, type PsDetail,
} from "../api/publicSearch";
import { useForcedTheme } from "../theme/AppearanceContext";
import { ResponsiveTable } from "../components/responsiveTable/ResponsiveTable";

export default function PublicSearch() {
  const { t } = useTranslation();
  useForcedTheme("clean");
  const { token = "" } = useParams();
  const [valid, setValid] = useState<boolean | null>(null);
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<PsDetail[]>([]);
  const [notFound, setNotFound] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState<Record<number, PsDetail>>({});

  useEffect(() => {
    psPing(token).then(setValid).catch(() => setValid(false));
  }, [token]);

  async function runSearch() {
    if (q.trim().length < 2) return;
    setBusy(true);
    try {
      setRows(await psSearch(token, q.trim()));
      setNotFound([]);
    } finally {
      setBusy(false);
    }
  }

  async function onFile(file: File | null) {
    if (!file) return;
    setBusy(true);
    try {
      const res = await psUploadXlsx(token, file);
      setRows(res.found);
      setNotFound(res.not_found.map((x) => x.value));
    } finally {
      setBusy(false);
    }
  }

  async function toggle(d: PsDetail) {
    if (expanded[d.id]) {
      setExpanded((e) => {
        const n = { ...e };
        delete n[d.id];
        return n;
      });
      return;
    }
    const full = await psDevice(token, d.id);
    setExpanded((e) => ({ ...e, [d.id]: full }));
  }

  async function doExport() {
    const blob = await psExport(token, rows.map((r) => r.dev_eui), notFound);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "module-search.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  }

  if (valid === null) {
    return (
      <Center h="100vh">
        <Loader />
      </Center>
    );
  }
  if (valid === false) {
    return (
      <Center h="100vh">
        <Text c="dimmed">{t("publicSearch.invalidLink")}</Text>
      </Center>
    );
  }

  return (
    <Container size="lg" py="xl">
      <Title order={3} mb="md">{t("publicSearch.title")}</Title>
      <Group mb="md">
        <TextInput
          style={{ flex: 1 }}
          placeholder={t("publicSearch.searchPlaceholder")}
          value={q}
          onChange={(e) => setQ(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void runSearch();
          }}
        />
        <Button onClick={() => void runSearch()} loading={busy}>
          {t("publicSearch.searchBtn")}
        </Button>
        <FileButton onChange={onFile} accept=".xlsx">
          {(props) => <Button variant="default" {...props}>{t("publicSearch.uploadBtn")}</Button>}
        </FileButton>
        {(rows.length > 0 || notFound.length > 0) && (
          <Button variant="light" onClick={() => void doExport()}>
            {t("publicSearch.exportBtn")}
          </Button>
        )}
      </Group>

      {rows.length === 0 && notFound.length === 0 ? (
        <Text c="dimmed">{t("publicSearch.noResults")}</Text>
      ) : (
        <ResponsiveTable
          forceMode="compact"
          mobile={{
            rows,
            rowKey: (d) => d.id,
            title: (d) => <Text span size="sm" fw={600}>{d.dev_eui}</Text>,
            subtitle: (d) => [d.model, d.serial_number].filter(Boolean).join(" · ") || undefined,
            badge: (d) => <Badge variant="light">{d.status}</Badge>,
            fields: [
              { label: t("publicSearch.colShipment"), render: (d) => d.shipment?.code ?? "—" },
              { label: t("publicSearch.colCustomer"), render: (d) => d.customer?.name ?? "—" },
              { label: t("publicSearch.colBranch"), render: (d) => d.branch ?? "—" },
              { label: t("publicSearch.colShipped"), render: (d) => d.shipped_at?.slice(0, 10) ?? "—" },
              {
                label: t("publicSearch.history"),
                hidden: (d) => !expanded[d.id],
                render: (d) => (
                  <Timeline bulletSize={14} lineWidth={2}>
                    {(expanded[d.id]?.history ?? []).map((h, i) => (
                      <Timeline.Item key={i} title={h.shipment_code ?? "—"}>
                        <Text size="sm">
                          {[h.customer_name, h.branch, h.reason].filter(Boolean).join(" · ")}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {t("publicSearch.historyEntered")}: {h.entered_at?.slice(0, 10) ?? "—"}
                          {" · "}
                          {t("publicSearch.historyLeft")}: {h.left_at?.slice(0, 10) ?? "—"}
                        </Text>
                      </Timeline.Item>
                    ))}
                  </Timeline>
                ),
              },
            ],
            actions: (d) => (
              <Group gap="xs" wrap="nowrap">
                <Button size="xs" variant="light" onClick={() => void toggle(d)}>
                  {expanded[d.id] ? "▼" : "▶"} {t("publicSearch.history")}
                </Button>
                <Button size="xs" variant="default" component="a" href={d.passport_url} target="_blank" rel="noreferrer">
                  {t("publicSearch.passport")} PDF
                </Button>
              </Group>
            ),
          }}
        >
        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th />
              <Table.Th>{t("publicSearch.colModel")}</Table.Th>
              <Table.Th>{t("publicSearch.colDevEui")}</Table.Th>
              <Table.Th>{t("publicSearch.colSerial")}</Table.Th>
              <Table.Th>{t("publicSearch.colStatus")}</Table.Th>
              <Table.Th>{t("publicSearch.colShipment")}</Table.Th>
              <Table.Th>{t("publicSearch.colCustomer")}</Table.Th>
              <Table.Th>{t("publicSearch.colBranch")}</Table.Th>
              <Table.Th>{t("publicSearch.colShipped")}</Table.Th>
              <Table.Th>{t("publicSearch.passport")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((d) => (
              <Fragment key={d.id}>
                <Table.Tr style={{ cursor: "pointer" }} onClick={() => void toggle(d)}>
                  <Table.Td>{expanded[d.id] ? "▼" : "▶"}</Table.Td>
                  <Table.Td>{d.model}</Table.Td>
                  <Table.Td>{d.dev_eui}</Table.Td>
                  <Table.Td>{d.serial_number}</Table.Td>
                  <Table.Td><Badge variant="light">{d.status}</Badge></Table.Td>
                  <Table.Td>{d.shipment?.code ?? "—"}</Table.Td>
                  <Table.Td>{d.customer?.name ?? "—"}</Table.Td>
                  <Table.Td>{d.branch ?? "—"}</Table.Td>
                  <Table.Td>{d.shipped_at?.slice(0, 10) ?? "—"}</Table.Td>
                  <Table.Td onClick={(e) => e.stopPropagation()}>
                    <a href={d.passport_url} target="_blank" rel="noreferrer">PDF</a>
                  </Table.Td>
                </Table.Tr>
                {expanded[d.id] && (
                  <Table.Tr>
                    <Table.Td colSpan={10}>
                      <Stack gap="xs" p="sm">
                        <Text fw={600}>{t("publicSearch.history")}</Text>
                        <Timeline bulletSize={14} lineWidth={2}>
                          {(expanded[d.id].history ?? []).map((h, i) => (
                            <Timeline.Item key={i} title={h.shipment_code ?? "—"}>
                              <Text size="sm">
                                {[h.customer_name, h.branch, h.reason].filter(Boolean).join(" · ")}
                              </Text>
                              <Text size="xs" c="dimmed">
                                {t("publicSearch.historyEntered")}: {h.entered_at?.slice(0, 10) ?? "—"}
                                {" · "}
                                {t("publicSearch.historyLeft")}: {h.left_at?.slice(0, 10) ?? "—"}
                              </Text>
                            </Timeline.Item>
                          ))}
                        </Timeline>
                      </Stack>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Fragment>
            ))}
          </Table.Tbody>
        </Table>
        </ResponsiveTable>
      )}

      {notFound.length > 0 && (
        <Stack gap={4} mt="md">
          <Text fw={600}>{t("publicSearch.notFoundTitle")} ({notFound.length})</Text>
          {notFound.map((v, i) => (
            <Text key={i} size="sm" c="red">{v}</Text>
          ))}
        </Stack>
      )}
    </Container>
  );
}
